import { FieldConfig } from '../components/TableSearch/types';
import {
  COL_PRODUCT_NAME,
  COL_CAS_NUMBER,
  COL_MANUFACTURER_NAME,
  COL_GRADE_NAME,
  COL_THERAPEUTIC_AREA_NAME,
  COL_FACILITY_NAME,
  COL_COUNTRY_CODE,
  COL_MOLECULAR_FORMULA,
  COL_MOLECULAR_MASS,
  COL_CREATED_DATE,
  COL_MODIFIED_DATE,
  COL_CITY,
  COL_STATE_PROVINCE,
  COL_POSTAL_CODE,
  COL_FEI_NUMBER,
  COL_DUNS_NUMBER,
  COL_QTA_STATUS,
  COL_FACILITY_STATUS,
  COL_QTA_EFFECTIVE_DATE,
  COL_QTA_EXPIRATION_DATE,
  COL_NOTE,
  COL_USER_ID,
  COL_URI,
  COL_INCHI,
  COL_SMILE,
  COL_CANONICAL_SMILE,
  COL_PRODUCT_SYNONYM,
  COL_REPLACED_RN,
  COL_ADDRESS_LINE1,
  COL_ADDRESS_LINE2,
  COL_COMMENTS,
  COL_AUDIT_DATE,
  COL_AUDIT_TYPE,
  COL_LATITUDE,
  COL_LONGITUDE,
  COL_AUDIT_ID,
  COL_TABLE_NAME,
  COL_RECORD_ID,
  COL_OPERATION_TYPE,
  COL_FIELD_NAME,
  COL_OLD_VALUE,
  COL_NEW_VALUE,
  COL_CHANGED_BY,
  COL_CHANGED_DATE,
  COL_CHANGE_REASON,
  COL_MANUFACTURING_STATUS,
} from '../constants/sqlColumns';

/**
 * Field configuration for Products advanced search
 * Defines all searchable fields, their types, and options
 */
export const productsFieldConfig: FieldConfig[] = [
  {
    key: COL_PRODUCT_NAME,
    label: 'Product Name',
    dataType: 'string',
  },
  {
    key: COL_CAS_NUMBER,
    label: 'CAS Number',
    dataType: 'string',
  },
  {
    key: COL_NOTE,
    label: 'Note',
    dataType: 'string',
  },
  {
    key: COL_USER_ID,
    label: 'Created By',
    dataType: 'string',
  },
  {
    key: COL_CREATED_DATE,
    label: 'Created Date',
    dataType: 'date',
  },
  {
    key: COL_MODIFIED_DATE,
    label: 'Modified Date',
    dataType: 'date',
  },
  {
    key: COL_URI,
    label: 'URI',
    dataType: 'string',
  },
  {
    key: COL_INCHI,
    label: 'InChI',
    dataType: 'string',
  },
  {
    key: COL_SMILE,
    label: 'SMILE',
    dataType: 'string',
  },
  {
    key: COL_CANONICAL_SMILE,
    label: 'Canonical SMILE',
    dataType: 'string',
  },
  {
    key: COL_MOLECULAR_FORMULA,
    label: 'Molecular Formula',
    dataType: 'string',
  },
  {
    key: COL_MOLECULAR_MASS,
    label: 'Molecular Mass',
    dataType: 'string',
  },
  {
    key: COL_PRODUCT_SYNONYM,
    label: 'Product Synonym',
    dataType: 'string',
  },
  {
    key: COL_REPLACED_RN,
    label: 'Replaced RN',
    dataType: 'string',
  },
  {
    key: COL_MANUFACTURER_NAME,
    label: 'Manufacturer',
    dataType: 'string',
  },
  {
    key: COL_FACILITY_NAME,
    label: 'Facility',
    dataType: 'string',
  },
  {
    key: COL_MANUFACTURING_STATUS,
    label: 'Manufacturing Status',
    dataType: 'selection',
    options: [
      { value: 'Commercial', label: 'Commercial' },
      { value: 'R&D', label: 'R&D' },
    ],
  },
  {
    key: COL_GRADE_NAME,
    label: 'Product Type',
    dataType: 'string',
  },
  {
    key: COL_THERAPEUTIC_AREA_NAME,
    label: 'Therapeutic Area',
    dataType: 'string',
  },
];

/**
 * Field configuration for Manufacturers advanced search
 */
export const manufacturersFieldConfig: FieldConfig[] = [
  {
    key: COL_MANUFACTURER_NAME,
    label: 'Manufacturer Name',
    dataType: 'string',
  },
  {
    key: COL_USER_ID,
    label: 'Created By',
    dataType: 'string',
  },
  {
    key: COL_CREATED_DATE,
    label: 'Created Date',
    dataType: 'date',
  },
  {
    key: COL_MODIFIED_DATE,
    label: 'Modified Date',
    dataType: 'date',
  },
  {
    key: COL_FACILITY_NAME,
    label: 'Facility Name',
    dataType: 'string',
  },
];

/**
 * Field configuration for Facilities advanced search
 */
export const facilitiesFieldConfig: FieldConfig[] = [
  {
    key: COL_FACILITY_NAME,
    label: 'Facility Name',
    dataType: 'string',
  },
  {
    key: COL_MANUFACTURER_NAME,
    label: 'Manufacturer Name',
    dataType: 'string',
  },
  {
    key: COL_FEI_NUMBER,
    label: 'FEI Number',
    dataType: 'string',
  },
  {
    key: COL_DUNS_NUMBER,
    label: 'DUNS Number',
    dataType: 'string',
  },
  {
    key: COL_ADDRESS_LINE1,
    label: 'Address Line 1',
    dataType: 'string',
  },
  {
    key: COL_ADDRESS_LINE2,
    label: 'Address Line 2',
    dataType: 'string',
  },
  {
    key: COL_CITY,
    label: 'City',
    dataType: 'string',
  },
  {
    key: COL_STATE_PROVINCE,
    label: 'State/Province',
    dataType: 'string',
  },
  {
    key: COL_POSTAL_CODE,
    label: 'Postal Code',
    dataType: 'string',
  },
  {
    key: COL_COUNTRY_CODE,
    label: 'Country Code',
    dataType: 'string',
  },
  {
    key: COL_QTA_STATUS,
    label: 'QTA Status',
    dataType: 'string',
  },
  {
    key: COL_QTA_EFFECTIVE_DATE,
    label: 'QTA Effective Date',
    dataType: 'date',
  },
  {
    key: COL_QTA_EXPIRATION_DATE,
    label: 'QTA Expiration Date',
    dataType: 'date',
  },
  {
    key: COL_COMMENTS,
    label: 'Comments',
    dataType: 'string',
  },
  {
    key: COL_AUDIT_DATE,
    label: 'Audit Date',
    dataType: 'date',
  },
  {
    key: COL_AUDIT_TYPE,
    label: 'Audit Type',
    dataType: 'string',
  },
  {
    key: COL_LATITUDE,
    label: 'Latitude',
    dataType: 'string',
  },
  {
    key: COL_LONGITUDE,
    label: 'Longitude',
    dataType: 'string',
  },
  {
    key: COL_FACILITY_STATUS,
    label: 'Facility Status',
    dataType: 'selection',
    options: [
      { value: 'APPROVED', label: 'APPROVED' },
      { value: 'INACTIVE', label: 'INACTIVE' },
      { value: 'DISQUALIFY', label: 'DISQUALIFY' },
      { value: 'R&D ONLY', label: 'R&D ONLY' },
      { value: 'ON HOLD', label: 'ON HOLD' },
      { value: 'NOT MANUF.', label: 'NOT MANUF.' },
    ],
  },
  {
    key: COL_USER_ID,
    label: 'Created By',
    dataType: 'string',
  },
  {
    key: COL_CREATED_DATE,
    label: 'Created Date',
    dataType: 'date',
  },
  {
    key: COL_MODIFIED_DATE,
    label: 'Modified Date',
    dataType: 'date',
  },
];

/**
 * Field configuration for Grades advanced search
 */
export const gradesFieldConfig: FieldConfig[] = [
  {
    key: COL_GRADE_NAME,
    label: 'Product Type',
    dataType: 'string',
  },
  {
    key: COL_USER_ID,
    label: 'Created By',
    dataType: 'string',
  },
  {
    key: COL_CREATED_DATE,
    label: 'Created Date',
    dataType: 'date',
  },
  {
    key: COL_MODIFIED_DATE,
    label: 'Modified Date',
    dataType: 'date',
  },
];

/**
 * Field configuration for Therapeutic Areas advanced search
 */
export const therapeuticAreasFieldConfig: FieldConfig[] = [
  {
    key: COL_THERAPEUTIC_AREA_NAME,
    label: 'Therapeutic Area Name',
    dataType: 'string',
  },
  {
    key: COL_USER_ID,
    label: 'Created By',
    dataType: 'string',
  },
  {
    key: COL_CREATED_DATE,
    label: 'Created Date',
    dataType: 'date',
  },
  {
    key: COL_MODIFIED_DATE,
    label: 'Modified Date',
    dataType: 'date',
  },
];

/**
 * Field configuration for Audit Logs advanced search
 */
export const auditLogsFieldConfig: FieldConfig[] = [
  {
    key: COL_AUDIT_ID,
    label: 'Audit ID',
    dataType: 'number',
  },
  {
    key: COL_TABLE_NAME,
    label: 'Table Name',
    dataType: 'selection',
    options: [
      { value: 'Manufacturers', label: 'Manufacturers' },
      { value: 'Facilities', label: 'Facilities' },
      { value: 'Grades', label: 'Product Type' },
      { value: 'Products', label: 'Products' },
      { value: 'therapeutic_areas', label: 'Therapeutic Areas' },
    ],
  },
  {
    key: COL_RECORD_ID,
    label: 'Record ID',
    dataType: 'number',
  },
  {
    key: COL_OPERATION_TYPE,
    label: 'Operation Type',
    dataType: 'selection',
    options: [
      { value: 'INSERT', label: 'INSERT' },
      { value: 'UPDATE', label: 'UPDATE' },
      { value: 'DELETE', label: 'DELETE' },
      { value: 'MERGE', label: 'MERGE' },
    ],
  },
  {
    key: COL_FIELD_NAME,
    label: 'Field Name',
    dataType: 'string',
  },
  {
    key: COL_OLD_VALUE,
    label: 'Old Value',
    dataType: 'string',
  },
  {
    key: COL_NEW_VALUE,
    label: 'New Value',
    dataType: 'string',
  },
  {
    key: COL_CHANGED_BY,
    label: 'Changed By',
    dataType: 'string',
  },
  {
    key: COL_CHANGED_DATE,
    label: 'Changed Date',
    dataType: 'date',
  },
  {
    key: COL_CHANGE_REASON,
    label: 'Change Reason',
    dataType: 'string',
  },
];
